/**
 * Determines if a color is considered light based on its luminance
 * @param color - Hex color string (with or without #)
 * @returns True if the color is light, false if dark
 */
export const isLightColor = (color: string): boolean => {
  const colorWithoutHash = color.replace('#', '')
  const fullHexColor =
    colorWithoutHash.length === 3
      ? colorWithoutHash + colorWithoutHash
      : colorWithoutHash

  const r = parseInt(fullHexColor.substring(0, 2), 16)
  const g = parseInt(fullHexColor.substring(2, 4), 16)
  const b = parseInt(fullHexColor.substring(4, 6), 16)

  return (r * 299 + g * 587 + b * 114) / 1000 > 155
}

/**
 * Validates if a string is a valid hex color format
 * @param color - String to validate
 * @returns True if valid hex color format (#RGB or #RRGGBB)
 */
export const isHexColor = (color: string): boolean => {
  return /^#([0-9A-F]{3}){1,2}$/i.test(color)
}

interface RGBColor {
  r: number
  g: number
  b: number
}

/**
 * Extracts a CSS custom property value and converts it to RGB color object
 * @param propertyName - CSS custom property name (with or without --)
 * @param element - Element to get computed styles from (defaults to document.documentElement)
 * @returns RGB color object or null if conversion fails
 */
export function getColorTokenValue(
  propertyName: string,
  element?: Element,
): RGBColor | null {
  if (typeof document === 'undefined') return null
  const targetElement = element ?? document.documentElement

  // Ensure property name starts with --
  const cssVarName = propertyName.startsWith('--')
    ? propertyName
    : `--${propertyName}`

  // Get the computed value
  const computedValue = getComputedStyle(targetElement)
    .getPropertyValue(cssVarName)
    .trim()

  if (!computedValue) {
    return null
  }

  return parseColorToRGB(computedValue)
}

/**
 * Parses various color formats to RGB
 * @param colorValue - Color string in various formats (hex, rgb, rgba, named color)
 * @returns RGB color object or null if parsing fails
 */
export function parseColorToRGB(colorValue: string): RGBColor | null {
  const trimmedValue = colorValue.trim()

  // Handle rgb() format
  const rgbMatch = trimmedValue.match(
    /^rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/,
  )
  if (rgbMatch) {
    return {
      r: parseInt(rgbMatch[1], 10),
      g: parseInt(rgbMatch[2], 10),
      b: parseInt(rgbMatch[3], 10),
    }
  }

  // Handle rgba() format (ignore alpha)
  const rgbaMatch = trimmedValue.match(
    /^rgba\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*[\d.]+\s*\)$/,
  )
  if (rgbaMatch) {
    return {
      r: parseInt(rgbaMatch[1], 10),
      g: parseInt(rgbaMatch[2], 10),
      b: parseInt(rgbaMatch[3], 10),
    }
  }

  // Handle hex format
  const hex = trimmedValue.replace('#', '')
  if (/^[0-9A-Fa-f]{6}$/.test(hex)) {
    return {
      r: parseInt(hex.substr(0, 2), 16),
      g: parseInt(hex.substr(2, 2), 16),
      b: parseInt(hex.substr(4, 2), 16),
    }
  }

  if (/^[0-9A-Fa-f]{3}$/.test(hex)) {
    return {
      r: parseInt(hex[0] + hex[0], 16),
      g: parseInt(hex[1] + hex[1], 16),
      b: parseInt(hex[2] + hex[2], 16),
    }
  }

  // Handle named colors using canvas technique
  return getNamedColorRGB(trimmedValue)
}

/**
 * Converts named colors to RGB using canvas rendering
 * @param colorName - CSS named color (e.g., 'red', 'blue', 'transparent')
 * @returns RGB color object or null if color name is invalid
 */
export function getNamedColorRGB(colorName: string): RGBColor | null {
  if (typeof document === 'undefined') return null
  const canvas = document.createElement('canvas')
  canvas.width = 1
  canvas.height = 1
  const ctx = canvas.getContext('2d')

  if (!ctx) return null

  ctx.fillStyle = colorName
  ctx.fillRect(0, 0, 1, 1)

  const imageData = ctx.getImageData(0, 0, 1, 1).data

  // Check if the color was actually applied (not black/transparent fallback)
  if (
    imageData[0] === 0 &&
    imageData[1] === 0 &&
    imageData[2] === 0 &&
    colorName !== 'black'
  ) {
    return null
  }

  return {
    r: imageData[0],
    g: imageData[1],
    b: imageData[2],
  }
}
